import { getResolvedSchedule, buildTimeGrid, DAY_GRID } from "@/services/doctorScheduleService";
import { formatDate } from "@/utils/datetime";

// Grade-base construída UMA vez (fonte única, vinda do service).
const BASE_TIME_GRID = buildTimeGrid();

/** Duração de um slot, em minutos — a mesma da grade. */
const SLOT_MIN = DAY_GRID.stepMinutes;
const FIM_DO_DIA = 24 * 60;

/**
 * DESTAQUE (não bloqueio) da disponibilidade do médico na clínica.
 * Compartilhado pelos diálogos de CRIAR e EDITAR atendimento.
 *
 * A disponibilidade NÃO restringe mais o agendamento — o médico precisa poder
 * encaixar emergências fora do horário. Aqui ela só serve para DESTACAR, na tela,
 * os dias e horários em que o médico atende (todos continuam selecionáveis).
 *
 * O componente que usar este mixin DEVE definir os computeds:
 *   scheduleClinicId  -> Number|null  (clínica em foco; null = sem regra)
 *   scheduleDoctorId  -> Number|null  (médico em foco)
 *   scheduleDate      -> 'YYYY-MM-DD'|null (data selecionada)
 *   scheduleBeginTime -> 'HH:mm'|''   (horário de início selecionado)
 *
 * A grade-base de horários (scheduleAllTimes) já vem do mixin, a partir da
 * FONTE ÚNICA em doctorScheduleService.buildTimeGrid(). O componente só precisa
 * sobrescrever esse computed se quiser uma grade diferente.
 *
 * E deve chamar resetSchedule() quando a clínica/médico mudar.
 */
export default {
  data() {
    return {
      dateMenu: false,
      pickerMonth: null, // "YYYY-MM" visível no date picker
      allowedDatesSet: new Set(), // datas com disponibilidade
      availabilityWindowsByDate: {}, // { 'YYYY-MM-DD': [{from,to}] }
      loadedMonths: new Set(),
      // salvia-772: faixas OCUPADAS por data, com o tipo do que ocupa
      // ("commitment" = compromisso do medico, "appointment" = consulta).
      // O `available` sozinho so diz o que esta livre, nao POR QUE nao esta --
      // e sem isso o seletor de horario nao consegue pintar de laranja nem
      // desabilitar a opcao.
      busyWindowsByDate: {},
      scheduleConfigured: false, // médico parametrizou ESTA clínica?
      availabilityHint: "",
    };
  },

  computed: {
    hasClinicDoctor() {
      return !!this.scheduleClinicId && !!this.scheduleDoctorId;
    },

    /** Grade-base de horários (fonte única). Componente pode sobrescrever. */
    scheduleAllTimes() {
      return BASE_TIME_GRID;
    },

    /** Janelas do dia selecionado. null = sem parametrização → nada bloqueia. */
    dayWindows() {
      if (!this.scheduleConfigured || !this.scheduleDate) return null;
      const w = this.availabilityWindowsByDate[this.scheduleDate];
      return w && w.length ? w : null;
    },

    /**
     * Horários de início: a grade-base (para encaixe de emergência) UNIDA aos
     * slots das janelas parametrizadas do dia. Assim os horários que o médico
     * cadastrou SEMPRE aparecem na lista (mesmo fora da grade fixa) e podem ser
     * destacados. Nada é bloqueado — só ampliamos as opções.
     */
    allowedBeginTimes() {
      const base = this.scheduleAllTimes || [];
      if (!this.dayWindows || !this.dayWindows.length) return base;

      const set = new Set(base);
      this.dayWindows.forEach((w) => {
        const end = this.timeToMin(w.to);
        for (let m = this.timeToMin(w.from); m < end; m += 30) {
          set.add(this.minToTime(m));
        }
      });
      // "HH:MM" com zero à esquerda ordena cronologicamente por string.
      return [...set].sort();
    },

    /**
     * Horários de fim: todos os posteriores ao início (sem restringir).
     *
     * NÃO é a lista de inícios. Um fim pode ser o horário em que o último slot
     * TERMINA: a grade começa consultas até 17:30, e a de 17:30 acaba às 18:00.
     * Antes o fim era tirado da lista de inícios, onde 18:00 não existe — o
     * campo ficava vazio ao escolher 17:30, e o mesmo no último horário de
     * cada janela cadastrada.
     */
    allowedEndTimes() {
      const fins = new Set(this.allowedBeginTimes);
      this.allowedBeginTimes.forEach((t) => {
        const fim = this.timeToMin(t) + SLOT_MIN;
        if (fim < FIM_DO_DIA) fins.add(this.minToTime(fim));
      });
      const todos = [...fins].sort();

      if (!this.scheduleBeginTime) return todos;
      const inicio = this.timeToMin(this.scheduleBeginTime);
      return todos.filter((t) => this.timeToMin(t) > inicio);
    },
  },

  watch: {
    pickerMonth(month) {
      this.loadAllowedDatesForMonth(month);
    },
  },

  methods: {
    /** Data dd/mm/aaaa para o campo ativador do picker. */
    formatDayDate: formatDate,
    timeToMin(t) {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    },
    minToTime(n) {
      return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(
        n % 60,
      ).padStart(2, "0")}`;
    },
    windowContaining(minutes) {
      return (this.dayWindows || []).find(
        (w) => this.timeToMin(w.from) <= minutes && minutes < this.timeToMin(w.to),
      );
    },

    /**
     * Fim sugerido ao escolher o início: um slot depois (30 min).
     *
     * Antes era "o próximo item da lista de inícios": vazio no último horário
     * (17:30 → nada) e um intervalo torto quando uma janela cadastrada
     * acrescentava horário fora da grade (08:15 → 08:30, 15 min).
     */
    computeEndTime(begin) {
      if (!begin) return "";
      const fim = this.timeToMin(begin) + SLOT_MIN;
      return fim < FIM_DO_DIA ? this.minToTime(fim) : "";
    },

    // ── DESTAQUE (não bloqueio) ──────────────────────────────────────

    /** Dia em que o médico atende nesta clínica → destacado no date picker. */
    isWorkingDay(dateStr) {
      return this.scheduleConfigured && this.allowedDatesSet.has(dateStr);
    },

    /** Faixas ocupadas do dia selecionado — salvia-772. */
    busyWindowsOfDay() {
      return this.busyWindowsByDate[this.scheduleDate] || [];
    },

    /**
     * O que ocupa este horário? "commitment", "appointment" ou null.
     *
     * Pertence à faixa quem COMEÇA dentro dela: [from, to). O fim exclusivo
     * importa — um compromisso das 14h às 15h não pode bloquear as 15h, que é
     * justamente quando ele acaba e o médico volta a ficar livre.
     */
    busyKindAt(t) {
      const m = this.timeToMin(t);
      const janela = this.busyWindowsOfDay().find(
        (w) => this.timeToMin(w.from) <= m && m < this.timeToMin(w.to),
      );
      return janela ? janela.kind : null;
    },

    /** Horário tomado por compromisso do médico (laranja, não selecionável). */
    isCommitmentTime(t) {
      return this.busyKindAt(t) === "commitment";
    },

    /** Horário tomado por qualquer coisa — compromisso ou consulta. */
    isBusyTime(t) {
      return this.busyKindAt(t) !== null;
    },

    // ── O mesmo, para a lista de FIM ────────────────────────────────
    //
    // Um horário de fim é julgado pelo trecho que TERMINA nele, não pelo que
    // começa: terminar às 18:00 quando outra consulta começa às 18:00 é
    // válido (encosta, não sobrepõe). Com as funções de início, esse 18:00
    // aparecia ocupado e desabilitado.

    /** O minuto imediatamente anterior a `t` — o fim do trecho que acaba em t. */
    minutoAntesDe(t) {
      return this.minToTime(Math.max(0, this.timeToMin(t) - 1));
    },

    isBusyEndTime(t) {
      return this.isBusyTime(this.minutoAntesDe(t));
    },

    isCommitmentEndTime(t) {
      return this.isCommitmentTime(this.minutoAntesDe(t));
    },

    isWorkingEndTime(t) {
      return this.isWorkingTime(this.minutoAntesDe(t));
    },

    /** Horário dentro de uma janela de atendimento do dia → destacado no select. */
    isWorkingTime(t) {
      if (!this.dayWindows) return false;
      const m = this.timeToMin(t);
      return this.dayWindows.some(
        (w) => this.timeToMin(w.from) <= m && m < this.timeToMin(w.to),
      );
    },

    async loadAllowedDatesForMonth(month) {
      if (!month || !this.hasClinicDoctor || this.loadedMonths.has(month)) return;

      const clinicId = this.scheduleClinicId;
      const doctorId = this.scheduleDoctorId;
      const [y, m] = month.split("-").map(Number);
      const lastDay = new Date(y, m, 0).getDate();

      try {
        const resolved = await getResolvedSchedule({
          clinicId,
          doctorIds: [doctorId],
          startDate: `${month}-01`,
          endDate: `${month}-${String(lastDay).padStart(2, "0")}`,
        });
        const configured = (resolved?.configured || []).includes(Number(doctorId));
        const info = (resolved?.doctors && resolved.doctors[doctorId]) || {};

        const dates = new Set(this.allowedDatesSet);
        const windows = { ...this.availabilityWindowsByDate };
        const busy = { ...this.busyWindowsByDate };

        (info.busy || []).forEach((b) => {
          (busy[b.date] = busy[b.date] || []).push({
            from: b.time_from,
            to: b.time_to,
            kind: b.kind,
          });
        });
        (info.available || []).forEach((w) => {
          dates.add(w.date);
          (windows[w.date] = windows[w.date] || []).push({
            from: w.time_from,
            to: w.time_to,
          });
        });

        // Reatribui (Set/objeto não são reativos por mutação)
        this.allowedDatesSet = dates;
        this.availabilityWindowsByDate = windows;
        this.busyWindowsByDate = busy;
        this.scheduleConfigured = configured;
        this.loadedMonths = new Set([...this.loadedMonths, month]);
        this.updateAvailabilityHint();
        this.onScheduleLoaded && this.onScheduleLoaded();
      } catch (e) {
        // Falha de rede não trava a UI — o backend continua sendo a autoridade.
        this.loadedMonths = new Set([...this.loadedMonths, month]);
      }
    },

    updateAvailabilityHint() {
      if (!this.scheduleConfigured) {
        this.availabilityHint = "Sem restrição de disponibilidade nesta clínica.";
        return;
      }
      const names = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      const present = new Set();
      this.allowedDatesSet.forEach((ds) => {
        const [yy, mm, dd] = ds.split("-").map(Number);
        present.add(new Date(yy, mm - 1, dd).getDay());
      });
      const list = [1, 2, 3, 4, 5, 6, 0]
        .filter((i) => present.has(i))
        .map((i) => names[i])
        .join(", ");
      this.availabilityHint = list ? `Atende nesta clínica: ${list}.` : "";
    },

    /** Limpa o cache e recarrega (chamar ao trocar clínica/médico). */
    resetSchedule() {
      this.allowedDatesSet = new Set();
      this.availabilityWindowsByDate = {};
      this.busyWindowsByDate = {};
      this.loadedMonths = new Set();
      this.scheduleConfigured = false;
      this.availabilityHint = "";
      const month =
        this.pickerMonth ||
        (this.scheduleDate ? this.scheduleDate.slice(0, 7) : null);
      if (month) this.loadAllowedDatesForMonth(month);
    },

    /** Data formatada dd/mm/aaaa para o campo ativador do picker. */
  },
};
